import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Refresh } from '@element-plus/icons-vue'
import { request } from '@/util/globalContext'

const FORM_DEFAULT = {
    id: null,
    code: '',
    name: '',
    level: null,
    parentCode: '',
    sortOrder: 0,
    isDeleted: '0'
}

export default function useAreaList() {
    const dialogVisible = ref(false)
    const formRef = ref(null)
    const formData = reactive({ ...FORM_DEFAULT })
    const areaTree = ref([])
    const refreshLoading = ref(false)
    const syncing = ref(false) // 是否正在同步
    let syncPollingTimer = null // 同步状态轮询定时器

    const treeProps = {
        children: 'children',
        label: 'label'
    }

    const formRules = {
        code: [{ required: true, message: '请输入地址编码', trigger: 'blur' }],
        name: [{ required: true, message: '请输入地址名称', trigger: 'blur' }]
    }

    // 懒加载节点
    const loadNode = async (node, resolve) => {
        try {
            const parentCode = node.level === 0 ? '' : node.data.value
            const res = await request.get('/area/getChildrenByParentCode', {
                params: { parentCode, isDeleted: 'ALL' }
            })
            if (res.code === 200 && res.data) {
                resolve(res.data)
            } else {
                resolve([])
            }
        } catch (error) {
            console.error('加载子节点失败', error)
            resolve([])
        }
    }

    // 加载根节点
    const loadRoot = async () => {
        refreshLoading.value = true
        areaTree.value = []
        loadNode({ level: 0 }, (data) => {
            areaTree.value = data
            refreshLoading.value = false
        })
    }

    // 添加子级
    const handleAddChild = (parentData) => {
        Object.assign(formData, {
            id: null,
            code: '',
            name: '',
            parentCode: parentData.value,
            level: (parentData.level || 1) + 1,
            sortOrder: 0,
            isDeleted: '0'
        })
        dialogVisible.value = true
    }

    // 切换状态
    const handleToggleStatus = async (data) => {
        const newStatus = data.isDeleted === '0' ? '1' : '0'
        const confirmMsg = newStatus === '1' 
            ? '确定要禁用该地址及其所有子地址吗？'
            : '确定要启用该地址及其所有父级和子地址吗？'
        
        ElMessageBox.confirm(confirmMsg, '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
        }).then(async () => {
            try {
                const res = await request.post('/area/toggleStatusWithRelated', {
                    id: data.id,
                    isDeleted: newStatus
                })

                if (res.code === 200) {
                    ElMessage.success(res.msg || '操作成功')
                    loadRoot()
                } else {
                    ElMessage.error(res.msg || '操作失败')
                }
            } catch (error) {
                ElMessage.error('操作失败')
            }
        }).catch(() => {})
    }

    // 删除
    const handleDelete = (data) => {
        ElMessageBox.confirm(`确定要删除“${data.label}”吗？`, '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
        }).then(async () => {
            try {
                const res = await request.post('/area/deleteArea', { id: data.id })
                if (res.code === 200) {
                    ElMessage.success('删除成功')
                    loadRoot()
                } else {
                    ElMessage.error(res.msg || '删除失败')
                }
            } catch (error) {
                ElMessage.error('删除失败')
            }
        }).catch(() => {})
    }

    // 提交保存
    const handleSubmit = async () => {
        if (!formRef.value) return

        await formRef.value.validate(async (valid) => {
            if (!valid) return

            try {
                const res = await request.post('/area/saveOrUpdate', formData)
                if (res.code === 200) {
                    ElMessage.success(res.msg || '保存成功')
                    dialogVisible.value = false
                    Object.assign(formData, FORM_DEFAULT)
                    loadRoot()
                } else {
                    ElMessage.error(res.msg || '保存失败')
                }
            } catch (error) {
                ElMessage.error('保存失败')
            }
        })
    }

    // 获取同步状态
    const getSyncStatus = async () => {
        try {
            const res = await request.get('/area/getSyncStatus')
            if (res.code === 200) {
                syncing.value = res.data
            }
        } catch (error) {
            console.error('获取同步状态失败', error)
        }
    }

    // 获取状态并检查是否完成
    const getSyncStatusAndCheck = async () => {
        try {
            const res = await request.get('/area/getSyncStatus')
            if (res.code === 200) {
                const wasSyncing = syncing.value
                syncing.value = res.data
                if (wasSyncing && !syncing.value) {
                    // 同步完成
                    stopPollingSyncStatus()
                    loadRoot()
                    ElMessage.success('同步完成')
                }
            }
        } catch (error) {
            console.error('获取同步状态失败', error)
        }
    }

    // 开始轮询同步状态
    const startPollingSyncStatus = () => {
        if (syncPollingTimer) {
            clearInterval(syncPollingTimer)
        }
        syncPollingTimer = setInterval(() => {
            getSyncStatusAndCheck()
        }, 5000)
    }

    // 停止轮询
    const stopPollingSyncStatus = () => {
        if (syncPollingTimer) {
            clearInterval(syncPollingTimer)
            syncPollingTimer = null
        }
    }

    // 同步数据
    const handleSync = async () => {
        // 先检查是否正在同步
        await getSyncStatus()
        if (syncing.value) {
            ElMessage.warning('同步中，请稍后再试')
            return
        }

        ElMessageBox.confirm('同步数据会清空原有地址数据，确定要同步吗？', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
        }).then(async () => {
            try {
                // 立即标记同步中并开始轮询
                syncing.value = true
                startPollingSyncStatus()

                // 发起同步请求，等待结果（最多8秒超时）
                const res = await request.post('/area/syncAreaData')
                if (res.code === 200) {
                    ElMessage.success(res.msg || '同步成功')
                    loadRoot()
                } else if (res.msg && res.msg !== '同步中，请稍后再试') {
                    ElMessage.error(res.msg || '同步失败')
                }
            } catch (error) {
                // 8秒超时不显示错误，继续轮询状态
            }
        }).catch(() => {})
    }

    onMounted(() => {
        loadRoot()
        // 页面加载时检查同步状态
        getSyncStatus()
    })

    onUnmounted(() => {
        // 组件卸载时清理定时器
        stopPollingSyncStatus()
    })

    return {
        areaTree,
        treeProps,
        dialogVisible,
        formRef,
        formData,
        formRules,
        Refresh,
        syncing,
        refreshLoading,
        loadRoot,
        loadNode,
        handleSync,
        handleAddChild,
        handleToggleStatus,
        handleDelete,
        handleSubmit
    }
}
